"use client";

import React from "react";
import { Phone, Mail, MapPin, Clock, Loader, CircleCheck, MessageCircle, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
const inputClasses = "flex w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-400";
function ProjectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-12 text-center bg-accent/10 border border-border rounded-lg">
        <CircleCheck className="size-16 text-primary mb-6" />
        <h3 className="font-default text-3xl font-bold mb-2 text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground mb-8 max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-md font-default text-lg px-8 h-12 border-primary text-primary hover:bg-primary/10">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-lg tracking-wide text-foreground">Full Name</FormLabel>
              <FormControl>
                <input type="text" placeholder="Enter your name" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">Please provide your full legal or business name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-lg tracking-wide text-foreground">Corporate Email</FormLabel>
              <FormControl>
                <input type="email" placeholder="name@company.com" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">We will send the technical proposal to this address.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-lg tracking-wide text-foreground">Contact Number</FormLabel>
              <FormControl>
                <input type="tel" placeholder="+1 (000) 000-0000" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground/60">Include international area codes if applicable.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="project_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-lg tracking-wide text-foreground">Solution Interest</FormLabel>
              <FormControl>
                <select className={inputClasses} {...field}>
                  <option value="" disabled>Select a solution category</option>
                  <option value="Industrial Automation">Industrial Automation</option>
                  <option value="Smart Manufacturing">Smart Manufacturing</option>
                  <option value="Digital Transformation">Digital Transformation</option>
                  <option value="Other">Custom Software</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="project_details" rules={{
      required: "Project details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-lg tracking-wide text-foreground">Technical Requirements</FormLabel>
            <FormControl>
              <textarea placeholder="Describe your operational challenges, required integrations, and desired outcomes..." className={`${inputClasses} min-h-[120px] resize-y`} {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/60">Detailed specifications help our engineers provide accurate estimates.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-md h-14 text-xl font-default font-bold tracking-wider bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-400 shadow-lg shadow-primary/20">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : "Initialize Consultation"}
      </Button>
    </Form>;
}
export default function StartYourProject() {
  const contactDetails = [{
    icon: Phone,
    label: "Direct Line",
    value: "+86 13927478668"
  }, {
    icon: Mail,
    label: "Technical Support",
    value: "ivy@hysorter.com"
  }, {
    icon: MapPin,
    label: "Global HQ",
    value: "Tech Innovation Park, Hangzhou, China"
  }, {
    icon: Send,
    label: "WhatsApp",
    value: "+86 13927478668"
  }, {
    icon: MessageCircle,
    label: "WeChat",
    value: "WeChat ID: hysorter"
  }];
  const operatingHours = [{
    day: "Monday - Friday",
    hours: "09:00 - 18:00"
  }, {
    day: "Saturday",
    hours: "10:00 - 15:00"
  }, {
    day: "Sunday",
    hours: "Closed"
  }];
  return <section data-section-id="221457" id="connect-with-us" className="bg-background text-foreground py-16 lg:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          {}
          <div className="lg:col-span-5 flex flex-col space-y-12">
            <div>
              <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground mb-8 uppercase tracking-tight">
                Global <span className="text-primary">Connectivity</span>
              </h2>
              
              <div className="space-y-6">
                {contactDetails.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4 group">
                    <div className="p-3 bg-primary/10 rounded-lg border border-primary/20 group-hover:border-primary transition-all duration-400">
                      <item.icon className="size-6 text-primary" />
                    </div>
                    <div className="pt-1">
                      <h3 className="font-default text-xl font-bold text-foreground tracking-wide">
                        {item.label}
                      </h3>
                      <p className="text-muted-foreground mt-1 min-h-[1.5rem] font-default">
                        {item.value || "Not provided"}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="bg-card border border-border p-6 md:p-10 shadow-xl rounded-xl backdrop-blur-sm">
              <div className="mb-8 border-b border-border pb-6">
                <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground uppercase tracking-tight">
                  Connect <span className="text-primary">With Us</span>
                </h2>
                <p className="text-muted-foreground mt-3 text-lg font-default">
                  Submit your technical inquiry and our engineering team will respond with a comprehensive solution overview.
                </p>
              </div>

              <FormProvider formKey="connect_form_home" formId="connect_form_home" sectionName="connect-with-us" defaultValues={{
              name: "",
              email: "",
              phone: "",
              project_type: "",
              project_details: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              project_details: {
                required: true
              }
            }} submitText="Initialize Consultation" successMessage="Transmission successful. Our technical consultants have received your data and will initiate contact within 24 business hours.">
                <ProjectForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}